import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import Card from '../../components/Card';
import Button from '../../components/Button';
import apiService from '../../services/apiService';
import { Save, Package, Box, Plus, X } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useForm, useFieldArray, Controller } from 'react-hook-form';

interface Product {
  _id: string;
  name: string;
  price: number;
}

type EquipmentType = 'product' | 'kit';

interface FormData {
  type: EquipmentType;
  name: string;
  description: string;
  price: number;
  quantity?: number;
  category?: string;
  components: { productId: string; quantity: number }[];
}

const EditEquipment: React.FC = () => {
  const { id, type } = useParams<{ id: string; type: EquipmentType }>();
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const { register, handleSubmit, control, reset, watch, formState: { errors } } = useForm<FormData>({
    defaultValues: {
      type: type || 'product',
      name: '',
      description: '',
      price: 0,
      quantity: 0,
      category: '',
      components: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'components',
  });

  const watchType = watch('type');

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await apiService.products.getAll();
        const productsData = response?.items || [];
        setProducts(productsData);
      } catch (error) {
        console.error("Failed to load products:", error);
        toast.error("Erreur lors du chargement des produits.");
      }
    };

    const loadData = async () => {
      if (!id || !type) {
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      try {
        let dataToReset: Partial<FormData> = { type };
        if (type === 'product') {
          const response = await apiService.products.getById(id);
          const product = response?.data?.data?.product ?? response?.data?.product ?? response?.data?.data ?? response?.data;
          dataToReset = { ...dataToReset, ...product };
        } else { // type is 'kit'
          await fetchProducts(); // Ensure products are loaded for kit components
          const response = await apiService.kits.getById(id);
          const kit = response?.data?.data?.kit ?? response?.data?.kit ?? response?.data?.data ?? response?.data;
          dataToReset = { ...dataToReset, ...kit };
        }
        if (dataToReset) {
          reset(dataToReset);
        }
      } catch (error) {
        console.error(`Failed to load ${type}:`, error);
        toast.error(`Erreur lors du chargement de l\'élément.`);
        navigate('/admin/equipments');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [id, type, reset, navigate]);

  const onSubmit = async (data: FormData) => {
    if (!id) return;
    setIsSaving(true);
    try {
      if (data.type === 'product') {
        const { name, description, price, quantity, category } = data;
        await apiService.products.update(id, { name, description, price, quantity, category });
        toast.success('Produit mis à jour avec succès !');
      } else { // type is 'kit'
        const { name, description, price, components } = data;
        if (!components || components.length === 0) {
          toast.error('Un kit doit contenir au moins un produit.');
          setIsSaving(false);
          return;
        }
        await apiService.kits.update(id, { name, description, price, components });
        toast.success('Kit mis à jour avec succès !');
      }
      navigate('/admin/equipments');
    } catch (error: any) {
      console.error('Failed to update equipment:', error);
      toast.error(`Erreur lors de la mise à jour : ${error.message || 'Une erreur est survenue.'}`);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <Layout><div className="text-center py-8">Chargement de l\'équipement...</div></Layout>;
  }

  return (
    <Layout>
      <div className="space-y-8 pb-24">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <Package className="w-8 h-8 mr-3 text-primary-600" />
          Modifier un Équipement
        </h1>

        <Card className="p-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Type is not editable, just displayed */}
            <div className="p-3 bg-gray-100 rounded-md">
              <p className="text-sm font-medium text-gray-700">Modification d\'un: <span className="font-bold text-primary-600">{watchType === 'product' ? 'Produit' : 'Kit'}</span></p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">Nom *</label>
                <input id="name" type="text" {...register('name', { required: 'Le nom est requis' })} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3" />
                {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>}
              </div>
              <div>
                <label htmlFor="price" className="block text-sm font-medium text-gray-700">Prix *</label>
                <input id="price" type="number" step="0.01" {...register('price', { required: 'Le prix est requis', valueAsNumber: true })} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3" />
                {errors.price && <p className="mt-1 text-sm text-red-600">{errors.price.message}</p>}
              </div>
            </div>
            
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">Description</label>
              <textarea id="description" {...register('description')} rows={3} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"></textarea>
            </div>

            {watchType === 'product' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="quantity" className="block text-sm font-medium text-gray-700">Quantité en stock</label>
                  <input id="quantity" type="number" {...register('quantity', { valueAsNumber: true, min: { value: 0, message: 'La quantité ne peut être négative' } })} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3" />
                  {errors.quantity && <p className="mt-1 text-sm text-red-600">{errors.quantity.message}</p>}
                </div>
                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700">Catégorie</label>
                  <input id="category" type="text" {...register('category')} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3" />
                </div>
              </div>
            )}

            {watchType === 'kit' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Box className="w-6 h-6 mr-2 text-primary-600" />
                  Composer le Kit
                </h3>
                {fields.map((field, index) => (
                  <div key={field.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-md">
                    <div className="flex-1">
                      <label className="text-sm font-medium text-gray-700">Produit</label>
                      <Controller
                        name={`components.${index}.productId`}
                        control={control}
                        rules={{ required: 'Veuillez sélectionner un produit' }}
                        render={({ field }) => (
                          <select {...field} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3">
                            <option value="">Sélectionner un produit</option>
                            {products.map(p => (
                              <option key={p._id} value={p._id}>{p.name} ({p.price ? `${p.price.toFixed(2)}€` : 'Prix non défini'})</option>
                            ))}
                          </select>
                        )}
                      />
                       {errors.components?.[index]?.productId && <p className="mt-1 text-sm text-red-600">{errors.components[index].productId.message}</p>}
                    </div>
                    <div className="w-32">
                      <label className="text-sm font-medium text-gray-700">Quantité</label>
                      <input type="number" {...register(`components.${index}.quantity`, { required: 'Qté requise', valueAsNumber: true, min: { value: 1, message: 'Min 1' } })} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3" />
                      {errors.components?.[index]?.quantity && <p className="mt-1 text-sm text-red-600">{errors.components[index].quantity.message}</p>}
                    </div>
                    <Button type="button" variant="destructive" size="sm" onClick={() => remove(index)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="ghost" onClick={() => append({ productId: '', quantity: 1 })}>
                  <Plus className="w-5 h-5 mr-2" />
                  Ajouter un produit au kit
                </Button>
              </div>
            )}

            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg z-10">
              <div className="max-w-4xl mx-auto flex justify-end space-x-3">
                <Button type="button" variant="ghost" onClick={() => navigate('/admin/equipments')}>
                  Annuler
                </Button>
                <Button type="submit" disabled={isSaving}>
                  <Save className="w-5 h-5 mr-2" />
                  {isSaving ? 'Enregistrement...' : 'Enregistrer les modifications'}
                </Button>
              </div>
            </div>
          </form>
        </Card>
      </div>
    </Layout>
  );
};

export default EditEquipment;
